import React, { useState } from "react";
import { CheckCircle2, Mail, Phone, MapPin, ExternalLink, Stethoscope, X, Grid3X3, FileText, Download } from "lucide-react";
import TiltCard from "./TiltCard";
import Carousel3D from "./Carousel3D";
import { SectionTitle } from "./PageBanner";
import { collegeProfiles } from "../data/collegeContent";
import { useCollegeMedia } from "../hooks/useDriveMedia";
import { formatSize } from "../lib/driveMedia";

// Photographs the college uploaded come first, then the ones built into the site. Keeping
// the built-in ones underneath means a college page is never empty, whether the college has
// uploaded nothing yet or Drive simply could not be reached this time.

// Google offers a Drive photograph at two different addresses and sometimes refuses one of
// them. Swapping to the other costs nothing and rescues the picture; a built-in photograph
// carries no second address and so is left alone.
function onImageError(e) {
  const img = e.currentTarget;
  const spare = img.dataset.fallback;
  if (!spare || img.src === spare) return;
  img.dataset.fallback = ""; // one attempt only, or a broken pair would loop
  img.src = spare;
}

// Rich profile block for a college: about, highlights, departments, contact + HD photo gallery
export default function CollegeProfile({ slug, college }) {
  const p = collegeProfiles[slug];
  const [active, setActive] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const media = useCollegeMedia(slug);
  if (!p) return null;
  const gallery = [...media.photos, ...p.gallery];
  const photos = showAll ? gallery : gallery.slice(0, 8);
  const documents = media.documents;

  return (
    <>
      <section style={{ padding: "10px 24px 30px" }} data-testid="college-profile">
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 26, alignItems: "start" }} className="two-col">
          <div>
            <SectionTitle>About the College</SectionTitle>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#EA580C", letterSpacing: 0.6, marginBottom: 10 }}>{p.tagline}</div>
            {p.about.map((para, i) => (
              <p key={i} style={{ color: "#44403C", fontSize: 15, lineHeight: 1.9, textAlign: "justify", marginBottom: 12 }}>{para}</p>
            ))}
            <SectionTitle style={{ marginTop: 26 }}>Departments</SectionTitle>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }} data-testid="college-departments">
              {p.departments.map(d => (
                <span key={d} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 999, background: "#FFF7ED", border: "1px solid rgba(249,115,22,0.3)", color: "#9A3412", fontSize: 12.5, fontWeight: 600 }}>
                  <Stethoscope size={12} color="#F97316" /> {d}
                </span>
              ))}
            </div>
          </div>
          <div style={{ display: "grid", gap: 18 }}>
            <TiltCard intensity={3}>
              <div style={{ padding: 22 }} data-testid="college-highlights">
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.2, color: "#EA580C" }}>KEY HIGHLIGHTS</div>
                <ul style={{ listStyle: "none", padding: 0, margin: "12px 0 0", display: "grid", gap: 10 }}>
                  {p.highlights.map((h, i) => (
                    <li key={i} style={{ display: "flex", gap: 10, fontSize: 14, color: "#1C1917", lineHeight: 1.5 }}>
                      <CheckCircle2 size={17} color={i % 2 ? "#FACC15" : "#F97316"} style={{ flexShrink: 0, marginTop: 2 }} /> {h}
                    </li>
                  ))}
                </ul>
              </div>
            </TiltCard>
            <TiltCard intensity={3}>
              <div style={{ padding: 22 }} data-testid="college-contact">
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.2, color: "#EA580C" }}>CONTACT</div>
                <div style={{ display: "grid", gap: 10, marginTop: 12, fontSize: 14, color: "#1C1917" }}>
                  <div style={{ display: "flex", gap: 10 }}><MapPin size={16} color="#F97316" style={{ flexShrink: 0, marginTop: 3 }} /> {p.contact.address}</div>
                  {p.contact.phone && <div style={{ display: "flex", gap: 10 }}><Phone size={16} color="#F97316" /> <a href={`tel:${p.contact.phone.replace(/\s/g, "")}`} style={{ color: "#1C1917", textDecoration: "none" }}>{p.contact.phone}</a></div>}
                  {p.contact.email && <div style={{ display: "flex", gap: 10, wordBreak: "break-all" }}><Mail size={16} color="#F97316" style={{ flexShrink: 0 }} /> <a href={`mailto:${p.contact.email}`} style={{ color: "#1C1917", textDecoration: "none" }}>{p.contact.email}</a></div>}
                  <a href={college.website} target="_blank" rel="noreferrer" data-testid="college-website-link" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#EA580C", fontWeight: 700, textDecoration: "none", marginTop: 4 }}>
                    <ExternalLink size={15} /> Official website
                  </a>
                </div>
              </div>
            </TiltCard>
          </div>
        </div>
      </section>

      <section style={{ padding: "0 24px 30px" }} className="soft-bg">
        <div style={{ maxWidth: 1200, margin: "0 auto", paddingTop: 30 }}>
          <SectionTitle>Campus Gallery — {college.name}</SectionTitle>
          <Carousel3D items={gallery.map(g => ({ url: g.url, title: g.title, category: college.district }))} height={420} onSelect={setActive} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "26px 0 12px" }}>
            <div style={{ fontWeight: 700, color: "#1C1917", display: "flex", alignItems: "center", gap: 8 }}><Grid3X3 size={18} color="#F97316" /> All photographs <span className="badge badge-yellow">{gallery.length} HD</span>{media.photos.length > 0 && <span className="badge badge-red" data-testid="drive-live-badge">{media.photos.length} live from Drive</span>}</div>
            {gallery.length > 8 && (
              <button className="btn-outline-3d" data-testid="college-gallery-toggle" onClick={() => setShowAll(v => !v)} style={{ padding: "8px 16px", fontSize: 13 }}>{showAll ? "Show less" : `View all ${gallery.length}`}</button>
            )}
          </div>
          <div className="grid-eq grid-4" style={{ display: "grid", gap: 14 }} data-testid="college-photo-grid">
            {photos.map((g, i) => (
              <TiltCard key={g.id || g.url} intensity={5}>
                <div data-testid={`college-photo-${i}`} onClick={() => setActive(g)} style={{ cursor: "pointer", borderRadius: 18, overflow: "hidden", position: "relative", aspectRatio: "4/3" }}>
                  <img src={g.url} alt={g.title} loading="lazy" onError={onImageError} data-fallback={g.fallback || ""} style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 600ms" }} onMouseEnter={e => e.currentTarget.style.transform = "scale(1.07)"} onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"} />
                  <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, transparent 60%, rgba(124,45,18,0.85))" }} />
                  <div style={{ position: "absolute", left: 12, right: 12, bottom: 10, color: "#fff", fontSize: 12.5, fontWeight: 700 }}>{g.title}</div>
                </div>
              </TiltCard>
            ))}
          </div>
        </div>
      </section>

      {documents.length > 0 && (
        <section style={{ padding: "0 24px 50px" }}>
          <div style={{ maxWidth: 1200, margin: "0 auto", paddingTop: 30 }}>
            <SectionTitle>Documents &amp; Downloads — {college.name}</SectionTitle>
            <p style={{ fontSize: 13, color: "#78716C", margin: "0 0 16px" }}>
              Circulars, notices and reports published by the college. {documents.length} file{documents.length === 1 ? "" : "s"}.
            </p>
            <div style={{ display: "grid", gap: 12 }} data-testid="college-documents">
              {documents.map((d, i) => (
                <div key={d.id || i} data-testid={`college-document-${i}`} className="premium-card circ-row" style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 18, alignItems: "center", padding: 18, borderLeft: "5px solid #F97316" }}>
                  <div className="icon-tile"><FileText size={20} /></div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                      <span className="badge badge-brown">{d.label}</span>
                      {d.path && <span style={{ fontSize: 12, color: "#78716C", fontWeight: 600 }}>{d.path}</span>}
                      {d.size > 0 && <span style={{ fontSize: 12, color: "#78716C" }}>{formatSize(d.size)}</span>}
                    </div>
                    <div className="font-serif" style={{ fontSize: 16, color: "#1C1917", fontWeight: 700, marginTop: 6, overflowWrap: "anywhere" }}>{d.title}</div>
                  </div>
                  <a href={d.download || d.view} target="_blank" rel="noreferrer" data-testid={`college-document-download-${i}`} className="btn-outline-3d" style={{ padding: "10px 14px", fontSize: 13, textDecoration: "none", display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }}>
                    <Download size={14} style={{ marginRight: 6 }} />View / Download
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {active && (
        <div onClick={() => setActive(null)} data-testid="college-lightbox" style={{ position: "fixed", inset: 0, background: "rgba(67,20,7,0.9)", backdropFilter: "blur(6px)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div onClick={e => e.stopPropagation()} style={{ maxWidth: "min(94vw, 1100px)", background: "#fff", borderRadius: 20, overflow: "hidden", position: "relative", boxShadow: "0 40px 80px rgba(0,0,0,0.5)", animation: "fadeUp 350ms ease-out" }}>
            <button data-testid="college-lightbox-close" onClick={() => setActive(null)} style={{ position: "absolute", right: 12, top: 12, background: "#DC2626", color: "#fff", border: "none", borderRadius: 10, padding: 8, cursor: "pointer", zIndex: 2 }}><X size={16} /></button>
            <img src={active.url} alt={active.title} onError={onImageError} data-fallback={active.fallback || ""} style={{ display: "block", maxWidth: "100%", maxHeight: "78vh", objectFit: "contain", margin: "0 auto", background: "#1C1917" }} />
            <div style={{ padding: "12px 18px", display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <span style={{ fontWeight: 700, color: "#1C1917", fontSize: 14 }}>{active.title}</span>
              {active.src && <a href={active.src} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: "#EA580C", fontWeight: 600, textDecoration: "none" }}>{active.live ? "Uploaded by college · Google Drive ↗" : "Source: official college website ↗"}</a>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
